import React, { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import { ImagesService } from '@services/image-service';
import Icon from '@components/atoms/icon/icon';
import Slider from '@components/atoms/slider/slider';
import { Picture } from '@interfaces/models/picture';
import ProductImage from '@components/atoms/vc-images/components/product-image';
import 'swiper/css';
import useWindowSize from '@hooks/use-window-size';
import useHeroPdpStyles from '@hooks/hero-pdp/use-hero-pdp-styles';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import useFeatureFlag from '@hooks/use-feature-flags';
import { useTranslation } from 'next-i18next';
import styles from './product-gallery.module.scss';

const PhotoSwipe = dynamic(() => import('./photo-swiper/photo-swiper'), {
  ssr: false,
});

export interface GalleryPicture {
  src: string;
  msrc?: string; // Used during zoom animation
  w: number;
  h: number;
}

interface GalleryProps {
  pictures?: Picture[];
  contextCssClass?: string;
  productPath: string;
  defaultAlt: string;
}

const ProductGallery: React.FC<GalleryProps> = (props) => {
  const { t: tA11y } = useTranslation('a11y');
  const { pictures, contextCssClass, productPath, defaultAlt } = props;
  const [image, setImage] = useState<Picture>(pictures?.[0]);
  const [prevButtonDisabled, setPrevButtonDisabled] = useState<boolean>(true);
  const [nextButtonDisabled, setNextButtonDisabled] = useState<boolean>(false);
  const [imageViewEventList, setImageViewEventList] = useState<number[]>([]);
  const [showImageSwiper, setShowImageSwiper] = useState<boolean>(false);
  const [swiperImages, setSwiperImages] = useState<Array<GalleryPicture>>([]);
  const [activePicIndex, setActivePicIndex] = useState<number>(0);
  const [currentIndex, setCurrentIndex] = useState<number>(0); // For A11Y
  const { isWiderThanMd } = useWindowSize();
  const { isHeroPdpStyles } = useHeroPdpStyles();
  const { isSemPDP } = useFeatureFlag();
  const { sendAnalyticEvent } = useAnalyticEvents('hero_pdp');

  const thumbNbToShowButtons: number = isHeroPdpStyles ? 5 : 6;

  const getSEOImgPath = (path: string): string => {
    return ImagesService.improveSEOProductPathImg(path, productPath);
  };

  const scrollableZone = useRef(null);
  const el = scrollableZone.current;

  const scrollToNext = () => {
    const el = scrollableZone.current;
    const top = el.scrollTop + el.offsetHeight;
    el.scrollTo({ top, behavior: 'smooth' });
  };

  const scrollToPrev = () => {
    const top = el.scrollTop - el.offsetHeight;
    el.scrollTo({ top, behavior: 'smooth' });
  };

  const onThumbScroll = () => {
    // This function will be triggered a lot, do not do complex computing inside, or use debounce.
    const { scrollTop, scrollHeight, offsetHeight } = el;
    setPrevButtonDisabled(scrollTop === 0);
    setNextButtonDisabled(scrollHeight - (scrollTop + offsetHeight) === 0);
  };

  const open = (imageSources: Array<GalleryPicture>, indexParam) => {
    setActivePicIndex(indexParam);
    setShowImageSwiper(true);
    setSwiperImages(imageSources);
  };

  const toGalleryPicture = (p: Picture): GalleryPicture => ({
    // Apparently 'produit' in the url is not a typo
    src: ImagesService.buildUrl(getSEOImgPath(`/produit/${p.path}`), { w: p.width || 1024 }, true),
    w: p.width || 1400, // TODO How do we define the height of the image (know it can be zoomed 2x) ?
    h: p.height || 1400,
  });

  const getPicIndex = (imgSrc: string, imageSources: Array<GalleryPicture>): number => {
    const imgName = imgSrc.split('/').slice(-1)[0];
    const nameArr = imageSources.map((e) => e.src.split('/').slice(-1)[0]);
    return nameArr.findIndex((n) => imgName.includes(n));
  };

  // used in mobile and pc
  const openDialog = (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
    argument?: Picture,
    index?: number,
  ) => {
    if (event && 'key' in event) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }
    const imageSources = pictures.map(toGalleryPicture);
    let picIndex = index;

    // If index is explicitly passed, use it
    if (index !== undefined) {
      picIndex = index;
    }
    // If called via keyboard on main image, use currentIndex state
    else if (event && 'key' in event) {
      picIndex = currentIndex;
    }
    // For mouse clicks, use currentIndex (reliable) or fallback to DOM extraction
    else if (event) {
      // Primary: Use currentIndex state (updated by handleHoverImage)
      picIndex = currentIndex;

      // Fallback: Try DOM extraction in case state is stale
      try {
        if (event.target instanceof HTMLElement) {
          const initiator = event.target.parentElement?.querySelector('img');
          if (initiator) {
            const extractedIndex = getPicIndex(initiator.src, imageSources);
            if (extractedIndex !== -1) {
              picIndex = extractedIndex;
            }
          }
        }
      } catch (e) {
        // Use currentIndex as fallback
      }
    }
    // Ultimate fallback
    else {
      picIndex = currentIndex;
    }
    sendAnalyticEvent('hero_pdp_photo_zoom', {
      value: picIndex + 1,
      label: '',
    });
    open(imageSources, picIndex);
  };

  // used in mobile
  const openImg = (argument: Picture, index: number) => {
    openDialog(null, argument, index);
  };

  const closePhotoSwiper = () => {
    setShowImageSwiper(false);
  };

  const handleHoverImage = (picture: Picture, index) => {
    setImage(picture);
    setCurrentIndex(index);
    if (index + 1 >= 2 && !imageViewEventList.includes(index)) {
      sendAnalyticEvent('hero_pdp_photo_view_vslider', {
        category: 'product',
        action: 'photo_view',
        label: 'vertical_slider',
        value: index + 1,
      });
      setImageViewEventList([...imageViewEventList, index]);
    }
  };

  useEffect(() => {
    setImage(pictures?.[0]);
  }, [pictures]);

  const handleThumbnailKeyDown =
    (index: number, pictures: Picture[], handleHoverImage: (pic: Picture, idx: number) => void) =>
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const buttons = e.currentTarget.parentElement?.querySelectorAll('[role="button"]') as
        | NodeListOf<HTMLElement>
        | undefined;
      if (!buttons) {
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const next = (index + 1) % pictures.length;
        buttons[next]?.focus();
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = (index - 1 + pictures.length) % pictures.length;
        buttons[prev]?.focus();
      }

      if (e.key === 'Enter' || e.code === 'Space') {
        e.preventDefault();
        handleHoverImage(pictures[index], index);
      }
    };

  return (
    <>
      <div
        className="visuallyhidden"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {currentIndex !== undefined && pictures[currentIndex] && (
          <>
            {`${tA11y('A11Y.PDP.SWIPER_PHOTOS', {
              currentImage: currentIndex + 1,
              currentImageName: pictures[currentIndex]?.alt || defaultAlt,
            })}, ${tA11y('A11Y.PDP.SWIPER_PHOTOS_TOTAL', { totalImages: pictures.length })}`}
          </>
        )}
      </div>
      <div className={clsx(styles.pdtImg, contextCssClass, { [styles['pdtImg--semPDPIteration']]: isSemPDP })}>
        <div
          className={clsx(styles.pdtImg__nav, {
            [styles['pdtImg__nav--heroPdp']]: isHeroPdpStyles,
          })}
        >
          {pictures?.length > thumbNbToShowButtons && (
            <button
              className={clsx(styles.thumbControl, styles.thumbControl__prev, 'leftRs')}
              type="button"
              disabled={prevButtonDisabled}
              onClick={scrollToPrev}
              tabIndex={prevButtonDisabled ? -1 : 0}
              aria-label={tA11y('A11Y.ICON.ARROW_PREVIOUS_ITEMS')}
            >
              <Icon
                name="arrow-head-up"
                ariaHidden
              />
            </button>
          )}
          <div
            className={styles.pdtImg__nav__thumbs}
            ref={scrollableZone}
            onScroll={onThumbScroll}
            aria-label={tA11y('A11Y.PDP.SWIPER_PHOTOS_TOTAL', {
              totalImages: pictures.length,
            })}
          >
            {pictures.map((picture: Picture, index) => (
              <div
                role="button"
                tabIndex={0}
                key={picture.path}
                className={clsx(styles.pdtImg__nav__thumb, {
                  [styles['pdtImg__nav__thumb--selected']]: index === currentIndex,
                })}
                onMouseEnter={() => handleHoverImage(picture, index)}
                aria-pressed={index === currentIndex}
                onKeyDown={handleThumbnailKeyDown(index, pictures, handleHoverImage)}
                onClick={() => handleHoverImage(picture, index)}
                aria-label={`${tA11y('A11Y.PDP.SWIPER_PHOTOS', {
                  currentImage: index + 1,
                  currentImageName: picture?.alt || defaultAlt,
                })}, ${tA11y('A11Y.PDP.SWIPER_PHOTOS_TOTAL', { totalImages: pictures.length })}`}
              >
                <div>
                  <ProductImage
                    sizesOverwrite={[128]}
                    src={getSEOImgPath(picture.path)}
                    width={60}
                    height={60}
                    quality={70}
                    alt={picture?.alt || defaultAlt}
                    type="thumbnail"
                    fetchpriority="low"
                  />
                </div>
              </div>
            ))}
          </div>
          {pictures?.length > thumbNbToShowButtons && (
            <button
              type="button"
              className={clsx(styles.thumbControl, styles.thumbControl__next, 'rightRs')}
              disabled={nextButtonDisabled}
              onClick={scrollToNext}
              tabIndex={nextButtonDisabled ? -1 : 0}
              aria-label={tA11y('A11Y.ICON.ARROW_MORE_ITEMS')}
            >
              <Icon
                name="arrow-head-down"
                ariaHidden
              />
            </button>
          )}
        </div>
        <div
          className={styles.pdtImg__main}
          role="button"
          aria-label={tA11y('A11Y.PDP.OPEN_SWIPER_PHOTOS_DIALOG', {
            currentImageName: pictures[currentIndex]?.alt || defaultAlt,
            currentImage: currentIndex + 1,
            totalImages: pictures.length,
          })}
          tabIndex={0}
          onKeyDown={openDialog}
        >
          <div
            className={clsx(styles.slide, styles.custom, {
              [styles['slide--heroPdp']]: isHeroPdpStyles,
            })}
            role="presentation"
            onClick={openDialog}
          >
            <ProductImage
              height={500}
              width={500}
              src={getSEOImgPath(image.path)}
              alt={image.alt}
              type="main"
              priority={isWiderThanMd}
              disableLazyLoad
              fetchpriority="high"
            />
          </div>
        </div>
      </div>
      <div
        className={clsx(styles.pdtImg__main, styles['pdtImg__main--control'], {
          [styles['pdtImg__main--heroPdp']]: isHeroPdpStyles,
        })}
        role="button"
        aria-label={tA11y('A11Y.PDP.OPEN_SWIPER_PHOTOS_DIALOG', {
          currentImageName: pictures[currentIndex]?.alt || defaultAlt,
          currentImage: currentIndex + 1,
          totalImages: pictures.length,
        })}
        tabIndex={0}
        onClick={openDialog}
      >
        <Slider
          clickIMG={openImg}
          pictures={pictures}
          productPath={productPath}
        />
      </div>
      {showImageSwiper && (
        <PhotoSwipe
          images={swiperImages}
          closeHandler={closePhotoSwiper}
          defaultActivePicIndex={activePicIndex}
          pictures={pictures}
        />
      )}
    </>
  );
};

export default ProductGallery;
